import { ACT_AS_HEADER } from '@classgo/shared';

import { setWaking } from '../shell/waking-state';
import type { ApiClient, ApiClientOptions, ApiErrorCode, ApiRequest } from './types';

/**
 * A refused or failed API call, with the envelope's stable code so screens and toasts can branch
 * on it without parsing messages.
 */
export class ApiError extends Error {
  public readonly code: ApiErrorCode;
  public readonly status: number;
  public readonly issues: readonly { path: string; message: string }[];
  public readonly details: Readonly<Record<string, unknown>> | null;

  constructor(
    code: ApiErrorCode,
    status: number,
    message: string,
    issues: readonly { path: string; message: string }[] = [],
    details: Readonly<Record<string, unknown>> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.issues = issues;
    this.details = details;
  }
}

const KNOWN_CODES: readonly ApiErrorCode[] = [
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VALIDATION_FAILED',
  'CONFLICT',
  'INTERNAL',
];

const isApiErrorCode = (value: unknown): value is ApiErrorCode =>
  typeof value === 'string' && (KNOWN_CODES as readonly string[]).includes(value);

const codeForStatus = (status: number): ApiErrorCode => {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 409) return 'CONFLICT';
  if (status === 400 || status === 422) return 'VALIDATION_FAILED';
  return status >= 500 ? 'INTERNAL' : 'UNKNOWN';
};

const toApiError = async (response: Response): Promise<ApiError> => {
  const payload: unknown = await response.json().catch(() => null);
  const envelope =
    typeof payload === 'object' && payload !== null && 'error' in payload
      ? (
          payload as {
            error: { code?: unknown; message?: unknown; issues?: unknown; details?: unknown };
          }
        ).error
      : null;
  const code = isApiErrorCode(envelope?.code) ? envelope.code : codeForStatus(response.status);
  const message =
    typeof envelope?.message === 'string' ? envelope.message : `HTTP ${response.status}`;
  const issues = Array.isArray(envelope?.issues)
    ? (envelope.issues as { path: string; message: string }[])
    : [];
  const details =
    typeof envelope?.details === 'object' &&
    envelope.details !== null &&
    !Array.isArray(envelope.details)
      ? (envelope.details as Record<string, unknown>)
      : null;
  return new ApiError(code, response.status, message, issues, details);
};

/**
 * Builds the API client: JSON in and out, the ID token as bearer, the error envelope turned into
 * `ApiError`, the act-as session header when staff act inside a school, and a 401 reported to
 * the host so the session can end and the router redirect.
 *
 * @param {ApiClientOptions} options -- Base URL, token source, 401 hook.
 * @returns The client.
 */
export const createApiClient = ({
  baseUrl,
  getToken,
  onUnauthorized,
  getActAsSession = () => null,
  fetchImpl = fetch,
}: ApiClientOptions): ApiClient => ({
  download: async (path: string) => {
    const token = getToken();
    if (!token) {
      onUnauthorized();
      throw new ApiError('UNAUTHORIZED', 401, 'No session');
    }
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    const actAs = getActAsSession();
    if (actAs) headers[ACT_AS_HEADER] = actAs;
    const response = await fetchImpl(`${baseUrl}${path}`, { method: 'GET', headers });
    if (!response.ok) {
      const error = await toApiError(response);
      if (error.status === 401) onUnauthorized();
      throw error;
    }
    const disposition = response.headers.get('content-disposition') ?? '';
    const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1];
    const plain = /filename="([^"]+)"/.exec(disposition)?.[1];
    return {
      blob: await response.blob(),
      filename: encoded ? decodeURIComponent(encoded) : (plain ?? 'descarga'),
    };
  },
  request: async <T>(path: string, init: ApiRequest = {}): Promise<T> => {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (init.body !== undefined) headers['content-type'] = 'application/json';
    if (init.auth !== false) {
      const token = getToken();
      if (!token) {
        onUnauthorized();
        throw new ApiError('UNAUTHORIZED', 401, 'No session');
      }
      headers['authorization'] = `Bearer ${token}`;
      const actAs = getActAsSession();
      if (actAs) headers[ACT_AS_HEADER] = actAs;
    }
    let response: Response;
    try {
      response = await fetchImpl(`${baseUrl}${path}`, {
        method: init.method ?? 'GET',
        headers,
        body: init.body === undefined ? null : JSON.stringify(init.body),
        signal: init.signal ?? null,
      });
    } catch (cause) {
      throw new ApiError('NETWORK', 0, cause instanceof Error ? cause.message : 'Network error');
    }
    if (!response.ok) {
      const error = await toApiError(response);
      if (error.status === 401) onUnauthorized();
      if (error.status === 503) setWaking(true);
      throw error;
    }
    setWaking(false);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  },
});
